
import React from "react";
import { FormField as FormFieldType } from "@/components/dashboard/shops/types";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

interface FieldControlsProps {
  field: FormFieldType;
  isEditing: boolean;
  toggleFieldRequired: (id: string) => void;
  deleteField: (id: string) => void;
  updateFieldType?: (id: string, type: FormFieldType['type']) => void;
}

export const FieldControls: React.FC<FieldControlsProps> = ({
  field,
  isEditing,
  toggleFieldRequired,
  deleteField,
  updateFieldType
}) => {
  const handleTypeChange = (value: string) => {
    if (updateFieldType) {
      updateFieldType(field.id, value as FormFieldType['type']);
    }
  };

  // Check if the field is a phone field (cannot be deleted or made non-required)
  const isPhoneField = field.type === 'phone' || 
                     field.name === 'customerPhone' || 
                     field.name === 'Téléphone' ||
                     field.name.toLowerCase().includes('phone') || 
                     field.name.toLowerCase().includes('téléphone');

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        {updateFieldType && (
          <Select 
            defaultValue={field.type} 
            onValueChange={handleTypeChange}
            disabled={isPhoneField} // Désactiver le sélecteur pour les champs téléphone
          >
            <SelectTrigger className="w-[120px] h-8">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Texte</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="phone">Téléphone</SelectItem>
              <SelectItem value="select">Liste</SelectItem>
              <SelectItem value="textarea">Zone de texte</SelectItem>
            </SelectContent>
          </Select>
        )}
        <div className="flex items-center gap-1">
          <Checkbox 
            id={`required-${field.id}`} 
            checked={field.required || isPhoneField} 
            onCheckedChange={() => !isPhoneField && toggleFieldRequired(field.id)}
            disabled={!field.enabled || isPhoneField}
          />
          <Label htmlFor={`required-${field.id}`} className="ml-1.5">
            Requis
          </Label>
        </div>
        {!isPhoneField && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => deleteField(field.id)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center text-sm text-gray-500">
        <span>
          {field.type === 'text' ? 'Texte' : 
           field.type === 'email' ? 'Email' : 
           field.type === 'phone' ? 'Téléphone' :
           field.type === 'textarea' ? 'Zone de texte' : 'Liste'}
        </span>
      </div>
      {(field.required || isPhoneField) && (
        <div className="flex items-center gap-1 text-sm">
          <div className="w-4 h-4 rounded bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <div className="w-2 h-2 bg-indigo-500 dark:bg-indigo-400 rounded-sm"></div>
          </div>
          <span>Requis</span>
        </div>
      )}
    </div>
  );
};
